/**
 * Chat-library UI constants for styling values not represented in shared design tokens.
 *
 * Colors are named with reference to the color constants defined in
 * packages/@benchling-nucleus-ui/src/tokens/Color.ts.
 */

import {Color} from '@benchling/nucleus-ui';

/**
 * Height for the chat input toolbar (plus, agent toggles, send) and contextual chips, aligned with
 * `ButtonSize.MINI` / `@height-mini--button` in `less/variables.less` (24px). Used only under
 * `llm/chat-library/` so other product buttons are unchanged.
 */
export const CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX = 30;

/**
 * Empty textarea floor. New chat keeps a taller invite box; an in-progress session uses a shorter
 * floor so the thread has more room. Typing still grows the box upward up to the max height.
 */
export const CHAT_INPUT_TEXTAREA_MIN_HEIGHT_NEW_CHAT_PX = 72;
export const CHAT_INPUT_TEXTAREA_MIN_HEIGHT_IN_CHAT_PX = 36;
export const CHAT_INPUT_TEXTAREA_MAX_HEIGHT_PX = 500;

/** Fill and initials colors for agent avatars. Nucleus has no token matching the fill. */
export const AGENT_AVATAR_COLORS = {
  backgroundColor: '#D0BFFF',
  color: Color.GRAY_900,
};

/** Shared maximum width for the chat thread, composer, and adjacent chat-aligned surfaces. */
export const CHAT_CONTENT_MAX_WIDTH_PX = 800;

/**
 * Maximum width of the AI settings modal's inner content column (form fields, lists, and headers).
 * Starts at the chat column width so settings content does not grow wider than Benchling AI chat.
 * Change this value to retune settings without affecting the chat thread.
 */
export const AI_SETTINGS_CONTENT_MAX_WIDTH_PX = CHAT_CONTENT_MAX_WIDTH_PX;

/** Minimum space on each side of that content column inside the AI settings modal. */
export const AI_SETTINGS_CONTENT_GUTTER_PX = 40;

/** Widest the AI settings modal grows. Below this it uses 90% of the browser width. */
export const AI_SETTINGS_MODAL_MAX_WIDTH_PX = 1200;

export const AI_SETTINGS_MODAL_WIDTH = '90vw';

/** Inherited from the AI settings modal. Unset outside it, so reused panels keep their own spacing. */
export const AI_SETTINGS_CONTENT_MAX_WIDTH_CSS_VAR = '--ai-settings-content-max-width';
export const AI_SETTINGS_CONTENT_GUTTER_CSS_VAR = '--ai-settings-content-gutter';

/**
 * Horizontal padding that centers the settings content column and keeps at least the gutter.
 * `fallback` is the existing padding used when the panel renders outside the AI settings modal.
 */
export function aiSettingsContentInlinePadding(fallback: string): string {
  return `max(var(${AI_SETTINGS_CONTENT_GUTTER_CSS_VAR}, ${fallback}), calc((100% - var(${AI_SETTINGS_CONTENT_MAX_WIDTH_CSS_VAR}, 100%)) / 2))`;
}

export const AI_BLUE_75 = '#F0F1FE';
export const AI_GRAY_50 = '#F7F7F9';
export const AI_GRAY_175 = '#E8E8EB';
export const AI_GRAY_200 = '#E2E3E5';
export const AI_GRAY_225 = '#D3D3DA';
export const AI_GRAY_290 = '#D3D3D6';
export const AI_GRAY_425 = '#B4B4B9';
export const AI_GRAY_475 = '#A4A4A5';
export const AI_GRAY_CHEVRON = 'rgba(23, 24, 35, 0.32)';

export const CHAT_INPUT_PLACEHOLDER_TEXT_STYLE = {
  color: AI_GRAY_425,
  fontSize: '16px',
  fontFamily: 'inherit',
  lineHeight: '24px',
} as const;

/** Redesigned chat sidebar inner content width (per mock). */
export const REDESIGN_SIDEBAR_CONTENT_WIDTH_PX = 175;

/** Drawer padding when open (`padding: '16px 14px'`). */
export const REDESIGN_DRAWER_VERTICAL_PADDING_PX = 16;
export const REDESIGN_DRAWER_HORIZONTAL_PADDING_PX = 14;

/** Width of the redesigned sidebar drawer when open (content + 2× horizontal padding). */
export const REDESIGN_DRAWER_WIDTH_PX =
  REDESIGN_SIDEBAR_CONTENT_WIDTH_PX + 2 * REDESIGN_DRAWER_HORIZONTAL_PADDING_PX;

/**
 * The resizable drawer can grow up to this fraction of the expanded panel's width, so the chat/
 * compose column always keeps the majority of the canvas.
 */
export const REDESIGN_DRAWER_MAX_WIDTH_RATIO = 0.4;

/** LocalStorage key persisting the user's manually-dragged drawer width across sessions. */
export const REDESIGN_DRAWER_WIDTH_STORAGE_KEY = 'llm-chat-redesign-drawer-width';

/**
 * CSS custom property that drives the resizable drawer's width (and the main column's matching left
 * inset). Set inline from state; the Aphrodite rules reference it so their `!important` declarations
 * still honor the dynamic value.
 */
export const REDESIGN_DRAWER_WIDTH_CSS_VAR = '--ai-drawer-width';

/**
 * Geometry of the redesigned header's icon chip (the rounded hover background drawn around the
 * hamburger, new-chat, settings, and expand/collapse glyphs). Every one of those controls builds its
 * chip from these values, so the glyphs land on the same pixels in the expanded and minimized views
 * and do not jump when the panel expands or collapses.
 *
 * The box matches `ui-library`'s small icon button (`@height-small--button` 28px, with
 * `(28 - 12) / 2` of padding) because the header also renders one of those: the share button.
 * ActionIcon glyphs keep that component's own size inside the box. Forcing them down to 12px
 * letterboxes wide icons and makes them draw smaller than the others.
 */
export const REDESIGN_HEADER_ICON_GLYPH_SIZE_PX = 12;
export const REDESIGN_HEADER_ICON_CHIP_PADDING_PX = 8;
export const REDESIGN_HEADER_ICON_CHIP_INSET_PX = 14;

/** Outer size of the icon chip, used to center it against a row and to size its container. */
export const REDESIGN_HEADER_ICON_CHIP_SIZE_PX =
  REDESIGN_HEADER_ICON_GLYPH_SIZE_PX + 2 * REDESIGN_HEADER_ICON_CHIP_PADDING_PX;

export const REDESIGN_HEADER_ICON_CHIP_RADIUS_PX = 4;

/** Icon chip colors: the glyph, and the rounded background drawn on hover or while active. */
export const REDESIGN_HEADER_ICON_COLOR = '#1B1B1F';
export const REDESIGN_HEADER_ICON_CHIP_BACKGROUND = '#E8EDFF';

/** Sidebar background + border, matching the redesign mock. */
export const REDESIGN_DRAWER_BACKGROUND = '#fafaff';
export const REDESIGN_DRAWER_BORDER = '#d6deff91';

/** Duration of the drawer open/close (width / max-height) transition, in ms. */
export const REDESIGN_DRAWER_TRANSITION_MS = 300;

/**
 * Faint inner shadow (BLUE_500 at ~6%) along the sidebar's trailing edges, so the drawer reads as a
 * recessed surface behind the chat column rather than a flat fill.
 */
export const REDESIGN_DRAWER_INNER_SHADOW = 'inset -1px -1px 3px #000db50a';
